<script setup lang="ts">
import { ref, watch } from 'vue'

import { TextInput } from '@collector/shared-ui'
import { formatTimePart } from '@collector/shared-utils'
import { useEventClock } from '@desktop/views/Relation/Sports/composables'

interface Props {
  type: 'minute' | 'second'
  inputClass?: string
}

const props = defineProps<Props>()

// emits
type Emits = {
  focus: []
  update: [
    newValue: string,
    oldValue: string,
    callback: (currentValue: string) => void,
  ]
}

const emit = defineEmits<Emits>()

// composables
const { eventTime, updateEventClockTime } = useEventClock()

// state
const isEditing = ref(false)
const lastValue = ref('')

// model
const model = defineModel<string>({ default: '' })

// methods
function onFocus({ event }: { event: FocusEvent }): void {
  isEditing.value = true
  lastValue.value = model.value

  const inputElement = event.target as HTMLInputElement
  inputElement.select()

  emit('focus')
}

function onBlur(): void {
  emit('update', model.value, lastValue.value, (currentValue) => {
    model.value = formatTimePart(parseInt(currentValue))

    updateEventClockTime()
  })

  isEditing.value = false
}

// init
watch(
  () => eventTime.value[props.type],
  (newValue) => {
    if (!isEditing.value) {
      model.value = formatTimePart(newValue)
    }
  },
  { immediate: true },
)
</script>

<template>
  <TextInput
    v-model="model"
    numeric
    variant="heavy"
    :inputClass="`${inputClass ? inputClass : ''} font-semibold text-xl!`"
    @keyup.enter="$event.target.blur()"
    @blur="onBlur"
    @focus="onFocus"
  />
</template>
